import { cn } from '../../utils/cn';

/**
 * One grade for every editorial picture.
 *
 * The directory's photographs come from hundreds of hands: cool interiors
 * beside warm ones, a saturated render beside a muted film still. Set as they
 * are, the grid reads as a contact sheet. This pulls them onto one palette —
 * warm, a little matte, the way the best of them already are — without
 * flattening any of them into the same picture.
 *
 * Three moves, each small so the sum reads as a house style rather than a
 * filter:
 *
 * - the picture itself is eased off: less saturation, a touch less contrast,
 *   a breath of sepia, so a hot render is no longer shouting beside a quiet
 *   photograph;
 * - a warm tan is laid over it in `color`, which replaces hue and saturation
 *   while leaving the light alone — so a cool blue room goes warm without
 *   losing its shadows and highlights. Kept well under half, so the picture's
 *   own colour still leads and a red door stays a red door;
 * - the page's own paper is laid over that in `soft-light`, which keeps the
 *   whole thing sitting in the paper rather than pasted on it. Kept light:
 *   it lifts the blacks, and the pictures want their depth.
 */
export const EDITORIAL_GRADE_MEDIA = cn(
    '[&_img]:[filter:saturate(0.86)_contrast(0.98)_brightness(1.01)_sepia(0.12)]',
);

const WARM_TAN = 'bg-[#c4a484]';
const PAPER = 'bg-[#f1f0ed]';

/**
 * The two washes, laid over the picture. Inert to the pointer and hidden from
 * assistive tech: they are a grade, not content, and anything the card puts
 * over its picture — a badge, a panel, a play control — goes on after them.
 */
export const EditorialGradeWash = () => (
    <>
        <div
            aria-hidden="true"
            className={cn(
                'pointer-events-none absolute inset-0 opacity-[0.26] mix-blend-color',
                WARM_TAN,
            )}
        />
        <div
            aria-hidden="true"
            className={cn(
                'pointer-events-none absolute inset-0 opacity-[0.16] mix-blend-soft-light',
                PAPER,
            )}
        />
    </>
);
